import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star } from "lucide-react";
import { QuestionRendererProps } from "./types";
import { GridRenderer } from "./GridRenderer";
import ModuleAvailabilityRenderer from "./ModuleAvailabilityRenderer";

const QuestionRenderer = ({
  question: q,
  answer,
  onAnswer,
  onMultiChoice,
  onGridChoice,
  options: rawOptions,
  isLoading,
  isWaiting,
  placeholderHint,
  lang = "en",
}: QuestionRendererProps) => {
  const disabled = isLoading || isWaiting;

  // Resolve translated options for choice types
  const options = (() => {
    if (lang === "sw" && q.options_sw && Array.isArray(q.options_sw) && q.options_sw.length === rawOptions.length) {
      return q.options_sw as string[];
    }
    return rawOptions;
  })();

  // Map display option back to the raw (English) value for storage
  const getValueForOption = (displayOpt: string) => {
    if (lang === "sw" && q.options_sw && Array.isArray(q.options_sw)) {
      const idx = (q.options_sw as string[]).indexOf(displayOpt);
      if (idx >= 0 && idx < rawOptions.length) return rawOptions[idx];
    }
    return displayOpt;
  };

  const getDisplayForValue = (val: string) => {
    if (lang === "sw" && q.options_sw && Array.isArray(q.options_sw)) {
      const idx = rawOptions.indexOf(val);
      if (idx >= 0 && idx < (q.options_sw as string[]).length) return (q.options_sw as string[])[idx];
    }
    return val;
  };

  // Short Text / Number
  if (q.question_type === "short_text" || q.question_type === "number") {
    return (
      <Input
        type={q.question_type === "number" ? "number" : "text"}
        value={(answer as string) || ""}
        onChange={e => onAnswer(e.target.value)}
        placeholder="Your answer"
      />
    );
  }

  // Long Text
  if (q.question_type === "long_text") {
    return (
      <Textarea
        value={(answer as string) || ""}
        onChange={e => onAnswer(e.target.value)}
        placeholder="Your answer"
        rows={4}
      />
    );
  }

  // Date
  if (q.question_type === "date") {
    return (
      <Input
        type="date"
        value={(answer as string) || ""}
        onChange={e => onAnswer(e.target.value)}
      />
    );
  }

  // Time
  if (q.question_type === "time") {
    return (
      <Input
        type="time"
        value={(answer as string) || ""}
        onChange={e => onAnswer(e.target.value)}
      />
    );
  }

  // Single Choice (radio)
  if (q.question_type === "single_choice" && q.options) {
    return (
      <>
        {placeholderHint && options.length === 0 && (
          <p className="text-xs text-muted-foreground italic">{placeholderHint}</p>
        )}
        <div className={`space-y-2 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
          {options.map((opt, i) => {
            const rawVal = rawOptions[i] || opt;
            return (
              <label key={rawVal} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={q.id}
                  value={rawVal}
                  checked={answer === rawVal}
                  onChange={() => onAnswer(rawVal)}
                  className="accent-primary"
                  disabled={disabled}
                />
                <span className="text-sm">{opt}</span>
              </label>
            );
          })}
        </div>
      </>
    );
  }

  // Multiple Choice (checkboxes)
  if (q.question_type === "multiple_choice" && q.options) {
    return (
      <>
        {placeholderHint && options.length === 0 && (
          <p className="text-xs text-muted-foreground italic">{placeholderHint}</p>
        )}
        <div className={`space-y-2 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
          {options.map((opt, i) => {
            const rawVal = rawOptions[i] || opt;
            return (
              <label key={rawVal} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={((answer as string[]) || []).includes(rawVal)}
                  onCheckedChange={(checked) => onMultiChoice(rawVal, !!checked)}
                  disabled={disabled}
                />
                <span className="text-sm">{opt}</span>
              </label>
            );
          })}
        </div>
      </>
    );
  }

  // Dropdown
  if (q.question_type === "dropdown" && q.options) {
    return (
      <Select value={(answer as string) || ""} onValueChange={v => onAnswer(v)} disabled={disabled}>
        <SelectTrigger className={disabled ? "opacity-50" : ""}>
          <SelectValue placeholder={placeholderHint || (lang === "sw" ? "Chagua jibu" : "Select an option")} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt, i) => {
            const rawVal = rawOptions[i] || opt;
            return <SelectItem key={rawVal} value={rawVal}>{opt}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    );
  }

  // Linear Scale
  if (q.question_type === "linear_scale") {
    const opts = q.options as string[];
    const min = parseInt(opts?.[0] || "1", 10);
    const max = parseInt(opts?.[1] || "5", 10);
    const lowLabel = opts?.[2] || "";
    const highLabel = opts?.[3] || "";
    const range = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          {lowLabel && <span className="text-xs text-muted-foreground">{lowLabel}</span>}
          {range.map(n => (
            <label key={n} className="flex flex-col items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={q.id}
                value={String(n)}
                checked={answer === String(n)}
                onChange={() => onAnswer(String(n))}
                className="accent-primary"
              />
              <span className="text-xs">{n}</span>
            </label>
          ))}
          {highLabel && <span className="text-xs text-muted-foreground">{highLabel}</span>}
        </div>
      </div>
    );
  }

  // Rating (Stars)
  if (q.question_type === "rating") {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onAnswer(String(star))}
            className="p-0.5"
          >
            <Star
              className={`h-6 w-6 ${parseInt(answer as string || "0", 10) >= star ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
            />
          </button>
        ))}
        {answer && <span className="text-sm text-muted-foreground ml-2">{answer}/5</span>}
      </div>
    );
  }

  // File Upload (placeholder)
  if (q.question_type === "file_upload") {
    return (
      <div>
        <Input type="file" onChange={e => onAnswer(e.target.files?.[0]?.name || "")} />
        <p className="text-xs text-muted-foreground mt-1">File upload is a placeholder — files are not stored yet.</p>
      </div>
    );
  }

  // Checkbox Grid / MCQ Grid
  if (q.question_type === "checkbox_grid" || q.question_type === "mcq_grid") {
    return (
      <GridRenderer
        question={q}
        answer={answer}
        onGridChoice={onGridChoice}
        lang={lang}
      />
    );
  }

  // Module Availability (Tutor Available Time)
  if (q.question_type === "module_availability") {
    const selectedModules: string[] = (q as any)._selectedModules || [];
    return (
      <ModuleAvailabilityRenderer
        selectedModules={selectedModules}
        answer={answer as Record<string, any>}
        onAnswer={onAnswer}
        lang={lang}
      />
    );
  }

  return null;
};

export default QuestionRenderer;
